import React, { useState, useEffect, useRef } from 'react';
import { api, listWeddingImages, uploadWeddingImage, deleteWeddingImage } from '../../../lib/api';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import MediaGrid from '../../../components/media/MediaGrid';
import ImageUploadButton from '../../../components/ui/ImageUploadButton';
import Button from '../../../components/ui/Button';

const MediaLibraryTab = ({ weddingId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [storageInfo, setStorageInfo] = useState({ totalSize: 0, storageLimit: 0, planId: 'free' });
  const { token } = useAdminAuth();
  const fileInputRef = useRef(null);

  const loadImages = async (cursor = null) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      let result;
      if (weddingId) {
        result = await listWeddingImages(token, weddingId, 50, cursor);
      } else {
        result = await api.listImages(token, 50, cursor);
      }
      if (cursor) {
        setImages((prev) => [...prev, ...result.images]);
      } else {
        setImages(result.images);
      }
      setNextCursor(result.nextCursor);
      if (result.totalSize !== undefined) {
        setStorageInfo({
          totalSize: result.totalSize,
          storageLimit: result.storageLimit || 0,
          planId: result.planId || 'free',
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadImages();
    }
  }, [token, weddingId]);

  const handleUploadSuccess = () => {
    loadImages();
  };

  const handleWeddingUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;

    setUploading(true);
    setError(null);
    try {
      await uploadWeddingImage(token, weddingId, file);
      loadImages();
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Storage limit exceeded')) {
        setError('Storage limit reached. Please delete some files or upgrade your plan to upload more.');
      } else {
        setError(msg);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!token) return;

    try {
      if (weddingId) {
        await deleteWeddingImage(token, weddingId, filename);
      } else {
        await api.deleteImage(filename, token);
      }
      setImages((prev) => prev.filter((img) => {
        const imgFilename = img.filename || img.key;
        return imgFilename !== filename;
      }));
    } catch (err) {
      alert(`Failed to delete image: ${err.message}`);
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Image URL copied to clipboard!');
    } catch (err) {
      alert('Failed to copy URL');
    }
  };

  const handleLoadMore = () => {
    if (nextCursor) {
      loadImages(nextCursor);
    }
  };

  const { totalSize, storageLimit, planId } = storageInfo;
  const usagePercent = storageLimit > 0 ? Math.min((totalSize / storageLimit) * 100, 100) : 0;
  const isNearLimit = usagePercent >= 80;
  const isAtLimit = usagePercent >= 100;

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const GB = 1024 * 1024 * 1024;
    const MB = 1024 * 1024;
    if (bytes >= GB) return (bytes / GB).toFixed(1) + ' GB';
    if (bytes >= MB) return (bytes / MB).toFixed(1) + ' MB';
    return (bytes / 1024).toFixed(1) + ' KB';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Media Library</h2>
        <p className="text-gray-600 mt-1">
          Manage uploaded images for your wedding invitation
        </p>
      </div>

      {/* Storage Usage */}
      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-gray-600">Storage Usage</p>
            <p className="text-lg font-bold">
              {formatSize(totalSize)} <span className="text-sm font-normal text-gray-500">/ {formatSize(storageLimit)}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Images</p>
            <p className="text-lg font-bold">{images.length}</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-blue-500'}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">{usagePercent.toFixed(1)}% used &middot; {planId === 'free' ? 'Free' : planId.charAt(0).toUpperCase() + planId.slice(1)} plan</p>
        {isNearLimit && !isAtLimit && (
          <p className="text-sm text-yellow-600 mt-2">You're approaching your storage limit. Consider upgrading your plan for more space.</p>
        )}
        {isAtLimit && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-medium">Storage limit reached. Delete files or upgrade your plan to upload more.</p>
            <a href={`/dashboard/weddings/${weddingId}?tab=settings&subtab=billing`} className="text-sm text-blue-600 underline mt-1 inline-block">Upgrade Plan</a>
          </div>
        )}
      </div>

      {/* Upload section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Upload New Image</h3>
        <div className="flex items-center gap-4">
          {weddingId ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleWeddingUpload}
                className="hidden"
                disabled={uploading}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || isAtLimit}
                loading={uploading}
              >
                {uploading ? 'Uploading...' : isAtLimit ? 'Storage Full' : 'Upload Image'}
              </Button>
            </>
          ) : (
            <ImageUploadButton
              onUploadSuccess={handleUploadSuccess}
              buttonText="Upload Image"
              buttonVariant="default"
            />
          )}
          <div className="text-sm text-gray-600">
            <p>Maximum file size: 10MB</p>
            <p>Supported formats: JPEG, PNG, WebP, GIF</p>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
        </div>
      )}

      {/* Images grid */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Uploaded Images</h3>
        <MediaGrid
          images={images}
          onDeleteImage={handleDeleteImage}
          onCopyUrl={handleCopyUrl}
          loading={loading}
        />

        {/* Load more */}
        {nextCursor && !loading && (
          <div className="mt-6 text-center">
            <Button onClick={handleLoadMore} variant="outline">
              Load More Images
            </Button>
          </div>
        )}

        {loading && images.length > 0 && (
          <div className="mt-6 text-center text-gray-500">
            Loading more images...
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaLibraryTab;
